import { useCallback, useEffect, useRef, useState } from "react";

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [bgParallax, setBgParallax] = useState(0);

  const handleScrollTo = useCallback((id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (sectionRef.current) {
            const rect = sectionRef.current.getBoundingClientRect();
            const sectionHeight = sectionRef.current.offsetHeight;
            // Map the section's vertical position to a [-1, 1] range
            // -1 when scrolled past (bottom at top), 1 when section top at viewport bottom
            const raw = rect.top / (window.innerHeight + sectionHeight);
            const clamped = Math.max(-1, Math.min(1, raw));
            setBgParallax(clamped * 70); // 70px max shift
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="accueil"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-24 md:pt-28 pb-14 md:pb-16"
    >
      {/* Parallax background layer */}
      <div
        className="absolute inset-0 z-0 will-change-transform"
        style={{ transform: `translate3d(0, ${bgParallax}px, 0)` }}
      >
        <img
          src="https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/0140e3f29521096742987eb6d45dd89a.png"
          alt="Marché de Noël Riviera Lalou Maestra"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      {/* Dark overlay for depth — visible immediately */}
      <div className="absolute inset-0 z-[1] bg-black/25" />

      {/* Coral-tinted overlay with blur for depth — visible immediately */}
      <div className="absolute inset-0 z-[2] bg-primary-500/40 backdrop-blur-[3px]" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-4 sm:px-8 max-w-[980px] mx-auto w-full">
        {/* Logo */}
        <div className="animate-hero-logo mb-4 md:mb-6">
          <img
            src="https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/86f582421ec807cbafd3a8ae32dbe706.png"
            alt="Lalou Maestra"
            className="h-24 sm:h-32 md:h-40 w-auto object-contain drop-shadow-2xl"
            style={{
              filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.25)) drop-shadow(0 0 60px rgba(234,81,73,0.08))",
            }}
          />
        </div>

        {/* Label */}
        <div className="animate-hero-label mb-4 md:mb-6">
          <span className="inline-flex items-center gap-3 md:gap-4 text-[11px] md:text-base tracking-[0.4em] uppercase text-white font-light">
            <span className="block w-6 md:w-8 h-px bg-white/50" />
            Event Planner
            <span className="block w-6 md:w-8 h-px bg-white/50" />
          </span>
        </div>

        {/* Main title */}
        <div className="animate-hero-title mb-8 md:mb-12">
          <h1 className="font-playfair text-[18px] sm:text-[22px] md:text-[2.5rem] lg:text-[3.25rem] font-bold text-white leading-[1.4] tracking-wide">
            <span className="block md:whitespace-nowrap mb-0 md:mb-0">Derrière chaque événement se cache une idée.</span>
            <span className="block whitespace-nowrap">Mon rôle est de{" "}
              <em className="font-bold italic">lui donner vie</em>
            </span>
          </h1>
        </div>

        {/* Body text */}
        <div className="animate-hero-body space-y-4 md:space-y-6 mb-6 md:mb-10 max-w-[780px]">
          <p className="text-white/90 text-[11px] sm:text-base md:text-[17px] leading-[1.9] font-light">
            Qu&apos;il naisse d&apos;une idée précise ou simplement de l&apos;envie de créer quelque chose d&apos;unique, chaque projet mérite une attention particulière. Chez Lalou Maestra, nous imaginons, concevons et coordonnons des événements sur mesure, pensés pour marquer les esprits et atteindre leurs objectifs.
          </p>
        </div>

        {/* Signature */}
        <div className="animate-hero-signature mb-8 md:mb-12 flex flex-col items-center gap-1">
          <p className="font-playfair text-white text-[15px] sm:text-lg md:text-xl italic tracking-wide">
            Lou Vanessa Matthey
          </p>
          <p className="text-white text-[9px] sm:text-[10px] md:text-[11px] tracking-[0.25em] uppercase font-light">
            Fondatrice &amp; Directrice
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="animate-hero-buttons flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          {/* Primary CTA */}
          <button
            onClick={() => handleScrollTo("#contact")}
            className="group relative cursor-pointer"
          >
            {/* Hover glow */}
            <div className="absolute inset-0 rounded-full blur-2xl bg-primary-400/20 scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            {/* Button body */}
            <div className="relative px-6 sm:px-12 md:px-14 py-3 sm:py-[1.125rem] md:py-5 border border-white/30 rounded-full bg-white/[0.04] backdrop-blur-sm transition-all duration-500 ease-out group-hover:border-white/50 group-hover:bg-white/[0.08] group-hover:scale-[1.04] group-hover:-translate-y-0.5">
              <span className="relative flex items-center gap-2 md:gap-4">
                <span className="text-white text-[10px] sm:text-sm font-light tracking-[0.2em] sm:tracking-[0.25em] uppercase whitespace-nowrap">
                  Donner vie à votre projet
                </span>
                <span className="block w-4 md:w-8 h-[1px] bg-white/40 transition-all duration-500 group-hover:w-8 md:group-hover:w-12 group-hover:bg-white/60" />
                <span className="text-white text-xs sm:text-base transition-all duration-500 group-hover:translate-x-1">
                  →
                </span>
              </span>
            </div>
          </button>

          {/* Secondary link */}
          <button
            onClick={() => handleScrollTo("#experiences")}
            className="group cursor-pointer text-white text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] uppercase font-light transition-all duration-500 hover:text-white px-4 py-3"
          >
            <span className="inline-flex items-center gap-2">
              NOS EXPÉRIENCES SIGNATURE
              <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
                →
              </span>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}